import { RefreshTokenErrorMessage, ApplicationCode } from "configs";
import { RestAPIClient } from "networks";
import { HTTPRequestMethods } from "networks/RestAPI/client";
import { loadRefreshToken, saveAccessToken, saveRefreshToken } from "utils";

import Paths from "./paths";
import {
  AuthGenerateCodeResponse,
  AuthGetMeResponse,
  AuthPermissionsRequest,
  AuthPermissionsResponse,
  AuthRefreshTokenRequest,
  AuthRefreshTokenResponse,
  AuthSignInRequest,
  AuthSignInResponse,
  AuthUserInfoResponse,
} from "./types";

export const authSignInService = (params: AuthSignInRequest) =>
  RestAPIClient.call<AuthSignInResponse>({
    method: HTTPRequestMethods.POST,
    url: Paths.SignIn,
    data: params,
  });

export const authGetMeService = () =>
  RestAPIClient.call<AuthGetMeResponse>({
    method: HTTPRequestMethods.GET,
    url: Paths.GetMe,
    config: {
      requireAuthentication: true,
      headers: {
        "x-application-code": ApplicationCode,
      },
    },
  });

export const authGetUserInfoService = () =>
  RestAPIClient.call<AuthUserInfoResponse>({
    method: HTTPRequestMethods.GET,
    url: Paths.UserInfo,
    config: {
      requireAuthentication: true,
      headers: {
        "x-application-code": ApplicationCode,
      },
    },
  });

export const authLoginService = (params: AuthSignInRequest) =>
  RestAPIClient.call<AuthSignInResponse>({
    method: HTTPRequestMethods.POST,
    url: Paths.Login,
    data: params,
    config: {
      headers: {
        "x-application-code": ApplicationCode,
      },
    },
  });

export const authGenerateOTPService = (email: string) =>
  RestAPIClient.call<AuthGenerateCodeResponse>({
    method: HTTPRequestMethods.POST,
    url: Paths.GenerateOTP,
    data: { email },
  });

export const authLogoutService = () =>
  RestAPIClient.call({
    method: HTTPRequestMethods.GET,
    url: Paths.Logout,
    config: {
      requireAuthentication: true,
      headers: { "x-application-code": ApplicationCode },
    },
  });

export const authRefreshTokenService = (params: AuthRefreshTokenRequest) =>
  RestAPIClient.call<AuthRefreshTokenResponse>({
    method: HTTPRequestMethods.POST,
    url: Paths.RefreshToken,
    data: params,
    config: { requireAuthentication: true },
  });

export const authRefreshTokenHandler = () => {
  return new Promise<{ accessToken?: string }>((resolve, rejects) => {
    const refreshToken = loadRefreshToken();
    if (!refreshToken) throw new Error(RefreshTokenErrorMessage);
    authRefreshTokenService({ refreshToken })
      .then((res) => {
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
          res?.data ?? {};
        if (!newAccessToken) return rejects;
        saveRefreshToken(newRefreshToken ?? refreshToken);
        saveAccessToken(newAccessToken);
        return resolve({ accessToken: newAccessToken });
      })
      .catch((error) => rejects(error));
  });
};

export const authPermissionsService = (params?: AuthPermissionsRequest) =>
  RestAPIClient.call<AuthPermissionsResponse>({
    method: HTTPRequestMethods.GET,
    url: Paths.Permissions,
    config: {
      requireAuthentication: true,
      headers: { "x-application": params?.applicationCode ?? null },
    },
  });
